import React, {useEffect, useRef} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {AuthContainer} from '../Auth.styled';
import {brand} from "../../../theme/brand";
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {useSnackbar} from '../../../contexts/SnackbarContext';
import {usePageTitle} from '../../../hooks/usePageTitle';
import {PageTitleKeys} from '../../../config/routeConstants';
import {Box, CircularProgress} from '@mui/material';
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {authService} from "../../../services/auth.service";

const AccountActivation = () => {
    usePageTitle(PageTitleKeys.AccountActivation);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {showSnackbar} = useSnackbar();
    const activationAttempted = useRef(false);

    useEffect(() => {
        if (activationAttempted.current) {
            return;
        }
        activationAttempted.current = true;

        const token = searchParams.get('token');

        if (!token) {
            navigate('/login', {replace: true});
            return;
        }

        const activateAccount = async () => {
            try {
                await authService.activateAccount(token);

                showSnackbar(t('accountActivation.successMessage'), 'success');
                navigate("/email-confirmed");
            } catch (error) {
                showSnackbar(parseError(error), "error");
                navigate('/login', {replace: true});
            }
        };

        void activateAccount();
    }, [searchParams, navigate, showSnackbar, t]);

    return (
        <AuthContainer role="region" aria-labelledby="account-activation-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={true} />

            <h2 id="account-activation-title" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                {t('accountActivation.title')}
            </h2>

            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="300px" 
                marginTop="24px"
                aria-live="polite"
                aria-atomic={true}
            >
                <CircularProgress size={120} thickness={4} aria-hidden={true} color="primary" />

                <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                    {t('auth.loading')}
                </Box>
            </Box>
        </AuthContainer>
    );
};

export default AccountActivation;
